/* eslint-disable react-hooks/immutability */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';

// ── Constants ──────────────────────────────────────────────────────────────────
const IS_SUPPORTED = !!navigator.mediaDevices?.getDisplayMedia;

// Multipliers applied to normalised band levels before passing to WebGL draw
// functions. Calibrated against real screen-share audio levels — raw values
// are typically 5–20× lower than what the effect thresholds expect.
const AUDIO_MULT = { bass: 12, mids: 3, highs: 2.5 };

// Vortex structural constants (never change at runtime)
const VORTEX_RING_COUNT  = 14;
const VORTEX_DRAMA_FLASH = 10;
const VORTEX_SUB_COUNT   = 11;

// Runtime-adjustable config — mutate PROPERTIES only, never reassign the object.
// Property mutation on a const object is not a variable reassignment and is safe
// to perform from event handlers in VisualizerDebugPanel.
const VORTEX_CFG = {
  baseSpeed:    0.00005, // ring z-advance per frame
  speedRange:   0.0010,  // extra speed from audio energy
  rotBase:      0.002,   // base rotation per frame (radians)
  rotMusic:     0.007,   // max extra rotation from mid-freq energy
  dramaThresh:  52,      // energy jump (0-255) to trigger palette shift
  dramaCooldown:9000,    // min ms between drama events
  subMinPeriod: 8000,   // ms — min time spent in each sub-mode
  subMaxPeriod: 16000,   // ms — max time before a forced switch
  fadeFrames:   20,      // frames to fade between sub-modes
};

// Module-level vortex animation state — shared with VisualizerDebugPanel.
// Wrap in an object so module-level helpers can mutate .current without
// reassigning the outer variable (avoids react-hooks/globals violations).
const VORTEX_STATE = { current: null };

// Module-level helpers that mutate VORTEX_STATE.current — must live here
// (not inside a component) to satisfy react-hooks/immutability.
function vortexJumpTo(i, locked) {
  if (!VORTEX_STATE.current) return;
  VORTEX_STATE.current.subMode = i;
  VORTEX_STATE.current.subAlpha = 1;
  VORTEX_STATE.current.fadeDir = 0;
  if (locked) VORTEX_STATE.current.nextSwitchAt = performance.now() + 1e9;
  vortexLoadModeCfg(i);
}
function vortexTriggerNext() {
  if (!VORTEX_STATE.current || VORTEX_STATE.current.fadeDir !== 0) return;
  VORTEX_STATE.current.pendingMode =
    (VORTEX_STATE.current.subMode + 1 + Math.floor(Math.random() * (VORTEX_SUB_COUNT - 1))) % VORTEX_SUB_COUNT;
  VORTEX_STATE.current.fadeDir = -1;
}
function vortexSetLockState(locked) {
  if (!VORTEX_STATE.current) return;
  VORTEX_STATE.current.nextSwitchAt =
    locked ? performance.now() + 1e9 : performance.now() + VORTEX_CFG.subMinPeriod;
}
function vortexReset() {
  VORTEX_STATE.current = null;
}
function vortexSetCfg(patch) {
  Object.assign(VORTEX_CFG, patch);
  if (VORTEX_STATE.current) Object.assign(VORTEX_MODE_CFG[VORTEX_STATE.current.subMode], patch);
}
// Per-mode config — each sub-mode remembers its own settings; seeded from VORTEX_CFG defaults.
// Defined after vortexSetCfg so the spread captures the initial values correctly.
const VORTEX_MODE_CFG = Array.from({ length: VORTEX_SUB_COUNT }, () => ({ ...VORTEX_CFG }));
function vortexLoadModeCfg(modeIdx) { Object.assign(VORTEX_CFG, VORTEX_MODE_CFG[modeIdx]); }

// Select a specific vortex sub-mode and lock it.  If the vortex state is not
// yet initialised the lazy-init will pick up _state.vortexSubMode instead.
function vortexSelectSubMode(idx) {
  patchState({ mode: 'vortex', vortexSubMode: idx });
  if (VORTEX_STATE.current) vortexJumpTo(idx, true);
}

// ── Three.js WebGL infrastructure ─────────────────────────────────────────────

// Window resize handler stored at module level so it can be removed on cleanup
function _threeOnResize() {
  const w = window.innerWidth, h = window.innerHeight;
  _threeRenderer.setSize(w, h);
  _threeCamera.aspect = w / h;
  _threeCamera.updateProjectionMatrix();
  if (_rtA) { _rtA.setSize(w, h); _rtB.setSize(w, h); }
  if (_tunnelCamera) { _tunnelCamera.aspect = w / h; _tunnelCamera.updateProjectionMatrix(); }
}

function initThree() {
  if (_threeInitialized) return;
  _threeRenderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
  // Cap pixel ratio to protect integrated GPUs from excessive fragment load
  _threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
  _threeRenderer.setClearColor(0x000000, 0);
  _threeRenderer.setSize(window.innerWidth, window.innerHeight);
  _threeCanvas = _threeRenderer.domElement;
  Object.assign(_threeCanvas.style, {
    position: 'fixed', inset: '0', width: '100vw', height: '100vh',
    zIndex: '-1', pointerEvents: 'none', display: 'none',
  });
  document.body.appendChild(_threeCanvas);
  _threeCamera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100);
  _threeCamera.position.z = 5;
  _threeScene = new THREE.Scene();
  _threeStartTime = performance.now();
  _dummy = new THREE.Object3D(); // reused for InstancedMesh — never new inside loop
  window.addEventListener('resize', _threeOnResize);
  _threeInitialized = true;
}

function switchMode(oldMode, newMode) {
  // Teardown old WebGL mode (no-op if not WebGL)
  if (oldMode === 'feedback') teardownFeedback();
  else if (oldMode === 'liquid') teardownLiquid();
  else if (oldMode === 'storm') teardownStorm();
  else if (oldMode === 'tunnel') teardownTunnel();

  if (WEBGL_MODES.includes(newMode)) {
    initThree();
    _threeCanvas.style.display = 'block';
    if (newMode === 'feedback') setupFeedback();
    else if (newMode === 'liquid') setupLiquid();
    else if (newMode === 'storm') setupStorm();
    else if (newMode === 'tunnel') setupTunnel();
  } else {
    if (_threeCanvas) _threeCanvas.style.display = 'none';
  }
}

// ── GLSL shaders ──────────────────────────────────────────────────────────────

// 3D Simplex Noise by Stefan Gustavson (public domain)
// Embedded inline — no separate file import needed.
const _SNOISE_GLSL = /* glsl */`
vec3 _mod289v3(vec3 x){return x-floor(x*(1./289.))*289.;}
vec4 _mod289v4(vec4 x){return x-floor(x*(1./289.))*289.;}
vec4 _permute(vec4 x){return _mod289v4(((x*34.)+1.)*x);}
vec4 _taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1./6.,1./3.);const vec4 D=vec4(0.,.5,1.,2.);
  vec3 i=floor(v+dot(v,C.yyy));vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz);vec3 l=1.-g;
  vec3 i1=min(g.xyz,l.zxy);vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;vec3 x2=x0-i2+C.yyy;vec3 x3=x0-D.yyy;
  i=_mod289v3(i);
  vec4 p=_permute(_permute(_permute(
    i.z+vec4(0.,i1.z,i2.z,1.))+
    i.y+vec4(0.,i1.y,i2.y,1.))+
    i.x+vec4(0.,i1.x,i2.x,1.));
  float n_=0.142857142857;vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z);vec4 y_=floor(j-7.*x_);
  vec4 x=x_*ns.x+ns.yyyy;vec4 y=y_*ns.x+ns.yyyy;
  vec4 h=1.-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy);vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.+1.;vec4 s1=floor(b1)*2.+1.;
  vec4 sh=-step(h,vec4(0.));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);vec3 p1=vec3(a0.zw,h.y);
  vec3 p2=vec3(a1.xy,h.z);vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=_taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.);
  m=m*m;
  return 42.*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}
`;

const LIQUID_VERT = /* glsl */`
precision mediump float;
${_SNOISE_GLSL}
uniform float uTime;
uniform float uBass;
uniform float uHighs;
uniform float uIntensity;
uniform float uSpeed;
varying vec3 vNormal;
varying float vDisplacement;
void main(){
  // Low-freq: large slow waves driven by bass
  float nLow  = snoise(normal*1.2+uTime*0.4*uSpeed)*uBass*1.5*uIntensity;
  // High-freq: sharp spikes driven by highs (second noise octave)
  float nHigh = snoise(normal*4.5+uTime*1.8*uSpeed)*uHighs*0.6*uIntensity;
  float disp=nLow+nHigh;
  vDisplacement=disp; vNormal=normal;
  gl_Position=projectionMatrix*modelViewMatrix*vec4(position+normal*disp,1.);
}
`;

const LIQUID_FRAG = /* glsl */`
precision mediump float;
uniform float uTime;
uniform float uMids;
uniform float uSpeed;
uniform float uColorBase;
varying float vDisplacement;
// Inline HSL→RGB conversion
vec3 hsl2rgb(float h,float s,float l){
  float c=(1.-abs(2.*l-1.))*s;
  float x=c*(1.-abs(mod(h*6.,2.)-1.));
  float m=l-c/2.;
  vec3 rgb;
  if(h<1./6.)      rgb=vec3(c,x,0.);
  else if(h<2./6.) rgb=vec3(x,c,0.);
  else if(h<3./6.) rgb=vec3(0.,c,x);
  else if(h<4./6.) rgb=vec3(0.,x,c);
  else if(h<5./6.) rgb=vec3(x,0.,c);
  else             rgb=vec3(c,0.,x);
  return rgb+m;
}
void main(){
  float hue=fract(vDisplacement*0.3+uTime*0.1*uSpeed+uMids*0.5+uColorBase);
  gl_FragColor=vec4(hsl2rgb(hue,0.9,0.55),1.);
}
`;

// ── Mode: tunnel — Doctor Who-style plasma vortex tunnel ─────────────────────

const TUNNEL_VERT = /* glsl */`
  uniform float uTime;
  uniform float uWobble;
  varying vec2 vUv;
  varying float vZ;
  void main() {
    vUv = uv;
    vec3 pos = position;
    pos.x += sin(pos.z * 0.05 + uTime * 2.0) * (2.0 * uWobble);
    pos.y += cos(pos.z * 0.05 + uTime * 1.5) * (2.0 * uWobble);
    vZ = pos.z;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const TUNNEL_FRAG = /* glsl */`
  uniform float uTime;
  uniform float uSpeed;
  uniform vec3 uColor1;
  uniform vec3 uColor2;
  varying vec2 vUv;
  varying float vZ;
  float hash(vec2 p) { return fract(1e4 * sin(17.0 * p.x + p.y * 0.1) * (0.1 + abs(sin(p.y * 13.0 + p.x)))); }
  float noise(vec2 x) {
    vec2 i = floor(x); vec2 f = fract(x);
    float a = hash(i); float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0)); float d = hash(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }
  float fbm(vec2 x) {
    float v = 0.0; float a = 0.5; vec2 shift = vec2(100.0);
    for (int i = 0; i < 5; ++i) { v += a * noise(x); x = x * 2.0 + shift; a *= 0.5; }
    return v;
  }
  void main() {
    vec2 uv = vUv * vec2(4.0, 1.0);
    uv.y += uTime * uSpeed * 0.5;
    uv.x += sin(uTime * 0.2) * 0.5;
    float n  = fbm(uv * 3.0);
    float n2 = fbm(uv * 5.0 - vec2(uTime * 0.1));
    float plasma = n * n2 * 2.5;
    float fog = smoothstep(-100.0, -20.0, vZ);
    vec3 finalColor = mix(uColor1, uColor2, n);
    gl_FragColor = vec4(finalColor * plasma, plasma * fog);
  }
`;

// Phase presets: plasma / ethereal / dark_matter
const TUNNEL_PHASES = [
  { c1: 0x3300ff, c2: 0xcc00ff, wobble: 1.0 },
  { c1: 0x00ffcc, c2: 0x000033, wobble: 2.5 },
  { c1: 0x330000, c2: 0xffaa00, wobble: 0.2 },
];

function _applyTunnelPhase(idx) {
  if (!_tunnelMesh) return;
  const { c1, c2, wobble } = TUNNEL_PHASES[idx];
  const u = _tunnelMesh.material.uniforms;
  u.uColor1.value.setHex(c1);
  u.uColor2.value.setHex(c2);
  u.uWobble.value = wobble;
}

function _tunnelCreateBolt() {
  while (_tunnelLightningGroup.children.length > 0) {
    _tunnelLightningGroup.remove(_tunnelLightningGroup.children[0]);
  }
  const points = [];
  let cur = new THREE.Vector3(
    (Math.random() - 0.5) * 15,
    (Math.random() - 0.5) * 15,
    -40 - Math.random() * 40,
  );
  points.push(cur);
  for (let i = 0; i < 10; i++) {
    cur = cur.clone().add(new THREE.Vector3(
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 4,
      5 + Math.random() * 5,
    ));
    points.push(cur);
  }
  const geo = new THREE.BufferGeometry().setFromPoints(points);
  const mat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1.0 });
  _tunnelLightningGroup.add(new THREE.Line(geo, mat));
  _tunnelBoltLight.position.copy(points[Math.floor(points.length / 2)]);
  _tunnelBoltLight.intensity = 100;
  _tunnelBoltLight.color.setHSL(Math.random(), 1.0, 0.8);
  return mat;
}

function setupTunnel() {
  _tunnelScene  = new THREE.Scene();
  _tunnelCamera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
  _tunnelCamera.position.z = 0;
  _tunnelPhaseIdx    = 0;
  _tunnelNextPhaseAt = 0;
  _tunnelLastTime    = 0;

  // Plasma tunnel cylinder
  const geo = new THREE.CylinderGeometry(10, 10, 200, 64, 64, true);
  geo.rotateX(Math.PI / 2);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    transparent: true,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uTime:   { value: 0 },
      uSpeed:  { value: 1.0 },
      uColor1: { value: new THREE.Color(0x3300ff) },
      uColor2: { value: new THREE.Color(0xcc00ff) },
      uWobble: { value: 1.0 },
    },
    vertexShader:   TUNNEL_VERT,
    fragmentShader: TUNNEL_FRAG,
  });
  _tunnelMesh = new THREE.Mesh(geo, mat);
  _tunnelScene.add(_tunnelMesh);

  // Glowing rings — pool of 6 deformable LINE rings, spawned one at a time on highs transients
  _tunnelRingPool = [];
  _tunnelHighsPrev = 0;
  _tunnelRingCooldown = 0;
  const RING_PTS = 128;
  for (let i = 0; i < 6; i++) {
    const positions = new Float32Array((RING_PTS + 1) * 3);
    for (let p = 0; p <= RING_PTS; p++) {
      const a = (p / RING_PTS) * Math.PI * 2;
      positions[p * 3]     = Math.cos(a) * 8;
      positions[p * 3 + 1] = Math.sin(a) * 8;
      positions[p * 3 + 2] = 0;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.LineBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0,
      blending: THREE.AdditiveBlending,
    });
    const line = new THREE.Line(geo, mat);
    line.visible = false;
    _tunnelScene.add(line);
    _tunnelRingPool.push({ mesh: line, geo, positions, active: false, z: 0, opacity: 0 });
  }

  // Lightning group + point light
  _tunnelLightningGroup = new THREE.Group();
  _tunnelScene.add(_tunnelLightningGroup);
  _tunnelBoltLight = new THREE.PointLight(0xffaaff, 0, 50);
  _tunnelScene.add(_tunnelBoltLight);
  _tunnelActiveBoltMat = null;
}

function teardownTunnel() {
  if (!_tunnelScene) return;
  // Dispose geometries — track seen geometries to avoid double-disposing shared ones
  const seenGeos = new Set();
  _tunnelScene.traverse((obj) => {
    if (obj.geometry && !seenGeos.has(obj.geometry)) {
      seenGeos.add(obj.geometry);
      obj.geometry.dispose();
    }
    if (obj.material) {
      if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
      else obj.material.dispose();
    }
  });
  _tunnelMesh = null;
  _tunnelRingPool = [];
  _tunnelLightningGroup = null;
  _tunnelBoltLight = null;
  _tunnelActiveBoltMat = null;
  _tunnelScene  = null;
  _tunnelCamera = null;
}

function drawTunnel(audio, elapsed) {
  if (!_tunnelScene || !_tunnelMesh) return;

  const delta = _tunnelLastTime > 0 ? elapsed - _tunnelLastTime : 0.016;
  _tunnelLastTime = elapsed;

  const speed = 0.5 + audio.bass * 2.0;

  // Auto-cycle phase every 12–20 s or on a strong bass hit
  if (elapsed > _tunnelNextPhaseAt || (audio.bass > 0.6 && elapsed > _tunnelNextPhaseAt - 8)) {
    _tunnelPhaseIdx   = (_tunnelPhaseIdx + 1) % TUNNEL_PHASES.length;
    _applyTunnelPhase(_tunnelPhaseIdx);
    _tunnelNextPhaseAt = elapsed + 12 + Math.random() * 8;
  }

  // Update shader
  const u = _tunnelMesh.material.uniforms;
  u.uTime.value  = elapsed;
  u.uSpeed.value = speed;

  // Spawn a ring on a high-frequency transient (spike detection with cooldown)
  const highsSpike = audio.highs > 0.22
    && audio.highs > _tunnelHighsPrev * 1.35
    && elapsed > _tunnelRingCooldown;
  _tunnelHighsPrev = audio.highs;
  if (highsSpike) {
    const ring = _tunnelRingPool.find(r => !r.active);
    if (ring) {
      ring.active = true;
      ring.z = -190;
      ring.mesh.position.z = -190;
      ring.mesh.rotation.x = (Math.random() - 0.5) * 0.15;
      ring.mesh.rotation.y = (Math.random() - 0.5) * 0.15;
      const { c1 } = TUNNEL_PHASES[_tunnelPhaseIdx];
      ring.mesh.material.color.setHex(c1);
      ring.mesh.visible = true;
      _tunnelRingCooldown = elapsed + 0.25;
    }
  }

  // Animate active rings — move toward camera, deform with bass oscillation
  const RING_PTS = 128;
  for (const ring of _tunnelRingPool) {
    if (!ring.active) continue;

    ring.z += 100 * speed * delta;
    ring.mesh.position.z = ring.z;

    // Fade in from spawn, fade out near camera
    const progress = (ring.z + 190) / 200; // 0=spawned, 1=at camera
    const fade = progress < 0.08 ? progress / 0.08 : progress > 0.75 ? 1 - (progress - 0.75) / 0.25 : 1;
    ring.mesh.material.opacity = Math.max(0, Math.min(1, fade * (0.45 + audio.bass * 0.55)));

    // Bass oscillation — deform ring vertices like 2D Oscillate but ~60% intensity
    const pts = ring.positions;
    const bassAmp = audio.bass * 8 * 0.13;
    const midAmp  = audio.mids * 8 * 0.07;
    for (let p = 0; p <= RING_PTS; p++) {
      const a = (p / RING_PTS) * Math.PI * 2;
      const osc = Math.sin(a * 3 + elapsed * 3.0) * bassAmp
                + Math.sin(a * 5 - elapsed * 2.3) * midAmp;
      const r = 8 + osc;
      pts[p * 3]     = Math.cos(a) * r;
      pts[p * 3 + 1] = Math.sin(a) * r;
    }
    ring.geo.attributes.position.needsUpdate = true;

    if (ring.z > 10) {
      ring.active = false;
      ring.mesh.visible = false;
      ring.mesh.material.opacity = 0;
    }
  }

  // Lightning — more frequent on highs
  if (Math.random() < (0.015 + audio.highs * 0.04) * speed) {
    _tunnelActiveBoltMat = _tunnelCreateBolt();
  }
  if (_tunnelActiveBoltMat) {
    _tunnelActiveBoltMat.opacity -= delta * 5;
    _tunnelBoltLight.intensity   -= delta * 300;
    if (_tunnelActiveBoltMat.opacity <= 0) _tunnelActiveBoltMat = null;
  }

  _threeRenderer.render(_tunnelScene, _tunnelCamera);
}

// ── Mode: feedback — ping-pong render targets with infinite trailing torus ────
// NOTE: Two renderer.render() calls per frame is expected and documented here.
// The first renders to a render target; the second blits to screen.

function setupFeedback() {
  const w = window.innerWidth, h = window.innerHeight;
  const rtOpts = {
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
  };
  _rtA = new THREE.WebGLRenderTarget(w, h, rtOpts);
  _rtB = new THREE.WebGLRenderTarget(w, h, rtOpts);

  // Torus wireframe in main scene
  const torusGeo = new THREE.TorusGeometry(1, 0.3, 16, 100);
  const edgesGeo = new THREE.EdgesGeometry(torusGeo);
  torusGeo.dispose(); // only need edges
  const torusMat = new THREE.LineBasicMaterial({ color: new THREE.Color().setHSL(0, 1, 0.6) });
  _feedbackTorusMesh = new THREE.LineSegments(edgesGeo, torusMat);
  _threeScene.add(_feedbackTorusMesh);

  // Separate orthographic scene for the fullscreen feedback blit quad
  _feedbackFbScene = new THREE.Scene();
  _feedbackFbCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const planeGeo = new THREE.PlaneGeometry(2, 2);
  _feedbackFbMat = new THREE.MeshBasicMaterial({
    map: _rtB.texture,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
  });
  _feedbackFbPlane = new THREE.Mesh(planeGeo, _feedbackFbMat);
  _feedbackFbScene.add(_feedbackFbPlane);
  _feedbackHue = 0;
}

function teardownFeedback() {
  if (_feedbackTorusMesh) {
    _feedbackTorusMesh.geometry.dispose();
    _feedbackTorusMesh.material.dispose();
    _threeScene.remove(_feedbackTorusMesh);
    _feedbackTorusMesh = null;
  }
  if (_feedbackFbPlane) {
    _feedbackFbPlane.geometry.dispose();
    _feedbackFbMat.dispose();
    _feedbackFbPlane = null; _feedbackFbMat = null;
    _feedbackFbScene = null; _feedbackFbCamera = null;
  }
  if (_rtA) { _rtA.dispose(); _rtA = null; }
  if (_rtB) { _rtB.dispose(); _rtB = null; }
}

function drawFeedback(audio) {
  if (!_feedbackTorusMesh || !_rtA || !_rtB) return;

  // Animate torus
  _feedbackTorusMesh.rotation.y += (0.01 + audio.highs * 0.05) * WEBGL_CFG.speed;
  _feedbackTorusMesh.rotation.x += 0.005 * WEBGL_CFG.speed;
  _feedbackTorusMesh.scale.setScalar(1 + audio.bass * 0.4 * WEBGL_CFG.intensity);
  _feedbackHue = (_feedbackHue + audio.mids * 0.005 * WEBGL_CFG.speed + 0.001) % 1;
  _feedbackTorusMesh.material.color.setHSL(_feedbackHue, 1, 0.6);

  // Step 1: Render previous frame (_rtB) scaled+faded into _rtA
  _threeRenderer.setRenderTarget(_rtA);
  _threeRenderer.clear();
  _feedbackFbMat.map = _rtB.texture;
  _feedbackFbMat.opacity = 0.95;
  _feedbackFbPlane.scale.set(1.01, 1.01, 1);
  _threeRenderer.render(_feedbackFbScene, _feedbackFbCamera);

  // Step 2: Add torus on top of the trails (no clear — accumulate)
  _threeRenderer.autoClear = false;
  _threeRenderer.render(_threeScene, _threeCamera);
  _threeRenderer.autoClear = true;

  // Step 3: Blit composite result (_rtA) to screen
  _threeRenderer.setRenderTarget(null);
  _feedbackFbMat.map = _rtA.texture;
  _feedbackFbMat.opacity = 1.0;
  _feedbackFbPlane.scale.set(1, 1, 1);
  _threeRenderer.render(_feedbackFbScene, _feedbackFbCamera);

  // Step 4: Swap targets — _rtB becomes the new "previous frame" for next tick
  const tmp = _rtA; _rtA = _rtB; _rtB = tmp;
}

// ── Mode: liquid — IcosahedronGeometry with vertex displacement shader ─────────

function setupLiquid() {
  const geo = new THREE.IcosahedronGeometry(2.0, 4);
  const mat = new THREE.ShaderMaterial({
    vertexShader: LIQUID_VERT,
    fragmentShader: LIQUID_FRAG,
    uniforms: {
      uTime:      { value: 0 },
      uBass:      { value: 0 },
      uMids:      { value: 0 },
      uHighs:     { value: 0 },
      uIntensity: { value: WEBGL_CFG.intensity },
      uSpeed:     { value: WEBGL_CFG.speed },
      uColorBase: { value: WEBGL_CFG.colorBase },
    },
  });
  _liquidMesh = new THREE.Mesh(geo, mat);
  _threeScene.add(_liquidMesh);
}

function teardownLiquid() {
  if (_liquidMesh) {
    _liquidMesh.geometry.dispose();
    _liquidMesh.material.dispose();
    _threeScene.remove(_liquidMesh);
    _liquidMesh = null;
  }
}

function drawLiquid(audio, elapsed) {
  if (!_liquidMesh) return;
  const u = _liquidMesh.material.uniforms;
  u.uTime.value      = elapsed;
  u.uBass.value      = audio.bass;
  u.uMids.value      = audio.mids;
  u.uHighs.value     = audio.highs;
  u.uIntensity.value = WEBGL_CFG.intensity;
  u.uSpeed.value     = WEBGL_CFG.speed;
  u.uColorBase.value = WEBGL_CFG.colorBase;
  _liquidMesh.rotation.y = elapsed * 0.2 * WEBGL_CFG.speed;
  _threeRenderer.render(_threeScene, _threeCamera);
}

// ── Mode: storm — 15,000-instance particle cloud (single draw call) ────────────

function setupStorm() {
  // Pre-compute sphere positions using Fibonacci sphere algorithm — skip if already done
  if (!_stormPositions) {
    _stormPositions = new Float32Array(STORM_COUNT * 3);
    _stormPhases    = new Float32Array(STORM_COUNT);
    const phi = Math.PI * (1 + Math.sqrt(5));
    for (let i = 0; i < STORM_COUNT; i++) {
      const theta = Math.acos(1 - 2 * (i + 0.5) / STORM_COUNT);
      const psi   = phi * i;
      const r     = 3.0;
      _stormPositions[i * 3]     = Math.sin(theta) * Math.cos(psi) * r;
      _stormPositions[i * 3 + 1] = Math.sin(theta) * Math.sin(psi) * r;
      _stormPositions[i * 3 + 2] = Math.cos(theta) * r;
      _stormPhases[i] = Math.random() * Math.PI * 2;
    }
  }
  const geo = new THREE.TetrahedronGeometry(0.04, 0);
  const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
  _stormMesh = new THREE.InstancedMesh(geo, mat, STORM_COUNT);
  // frustumCulled = false is required — Three.js frustum-culls by the mesh's own
  // bounding sphere which can be stale/wrong for instanced meshes.
  _stormMesh.frustumCulled = false;
  _threeScene.add(_stormMesh);
}

function teardownStorm() {
  if (_stormMesh) {
    _stormMesh.geometry.dispose();
    _stormMesh.material.dispose();
    _threeScene.remove(_stormMesh);
    _stormMesh = null;
    // Keep _stormPositions and _stormPhases — avoid recomputing on re-activation
  }
}

function drawStorm(audio, elapsed) {
  if (!_stormMesh || !_dummy) return;
  // Zero allocations in this loop — _dummy is reused every iteration
  const rotSpeed     = audio.bass * WEBGL_CFG.intensity * WEBGL_CFG.speed * 0.02;
  const particleScale = 1 + audio.highs * 3;
  for (let i = 0; i < STORM_COUNT; i++) {
    _dummy.position.set(
      _stormPositions[i * 3],
      _stormPositions[i * 3 + 1],
      _stormPositions[i * 3 + 2],
    );
    _dummy.rotation.y = elapsed * rotSpeed + _stormPhases[i];
    _dummy.scale.setScalar(particleScale);
    _dummy.updateMatrix();
    _stormMesh.setMatrixAt(i, _dummy.matrix);
  }
  _stormMesh.instanceMatrix.needsUpdate = true;
  _threeRenderer.render(_threeScene, _threeCamera);
}

// ── Module-level game context — set by WebRTCContext ──────────────────────────
// Allows VisualizerControls (rendered in Layout, outside WebRTCProvider) to know
// whether we're in a game, who the host is, and whether screen sharing is active.
// Default: inPersonMode: true → falls back to getDisplayMedia for everyone.
let _gameCtx = { isHost: false, inPersonMode: true, isHostScreenSharing: false, hostAudioStream: null };
const _gameCtxSubs = new Set();

export function setVisualizerGameContext(ctx) {
  _gameCtx = { ..._gameCtx, ...ctx };
  _gameCtxSubs.forEach((fn) => fn(_gameCtx));
}

function useVisualizerGameContext() {
  const [g, setG] = useState(_gameCtx);
  useEffect(() => {
    _gameCtxSubs.add(setG);
    return () => _gameCtxSubs.delete(setG);
  }, []);
  return g;
}

// ── WebGL mode config — mutate PROPERTIES only, never reassign ─────────────────
const WEBGL_CFG = { speed: 1.0, intensity: 1.0, colorBase: 0.0 };

// WebGL mode identifiers
const WEBGL_MODES = ['feedback', 'liquid', 'storm', 'tunnel'];

// ── Module-level audio graph singletons ───────────────────────────────────────
// Survive component unmount/remount between rounds so the user only enables once.
let _stream   = null;
let _audioCtx = null;
let _analyser = null;

// Frequency-separated analysers (for WebGL modes)
let _analyserBass = null, _analyserMids = null, _analyserHighs = null;
let _bassData = null, _midsData = null, _highsData = null;

// Debug mode — track per-channel max values (reset on cleanup)
const _debugMax = { bass: 0, mids: 0, highs: 0 };
const _debugSum = { bass: 0, mids: 0, highs: 0, count: 0 };

// ── Three.js module-level singletons ──────────────────────────────────────────
let _threeRenderer = null, _threeScene = null, _threeCamera = null;
let _threeCanvas = null, _threeInitialized = false, _threeStartTime = 0;
let _dummy = null; // reused Object3D for InstancedMesh — never new inside loop
// Feedback mode refs
let _feedbackTorusMesh = null, _feedbackFbScene = null, _feedbackFbCamera = null;
let _feedbackFbPlane = null, _feedbackFbMat = null, _feedbackHue = 0;
let _rtA = null, _rtB = null;
// Liquid mode ref
let _liquidMesh = null;
// Storm mode refs
let _stormMesh = null;
// Tunnel mode refs
let _tunnelScene = null, _tunnelCamera = null, _tunnelMesh = null;
let _tunnelRingPool = [], _tunnelLightningGroup = null;
let _tunnelBoltLight = null, _tunnelActiveBoltMat = null;
let _tunnelPhaseIdx = 0, _tunnelNextPhaseAt = 0, _tunnelLastTime = 0;
let _tunnelHighsPrev = 0, _tunnelRingCooldown = 0;
let _stormPositions = null, _stormPhases = null; // pre-computed, kept across re-activations
const STORM_COUNT = 15000;

function cleanupSingletons() {
  if (_audioCtx)  { _audioCtx.close(); _audioCtx = null; }
  if (_stream)    { _stream.getTracks().forEach((t) => t.stop()); _stream = null; }
  _analyser = null;
  // Band analysers (no explicit close needed — they share the AudioContext)
  _analyserBass = _analyserMids = _analyserHighs = null;
  _bassData = _midsData = _highsData = null;
  _debugMax.bass = _debugMax.mids = _debugMax.highs = 0;
  _debugSum.bass = _debugSum.mids = _debugSum.highs = _debugSum.count = 0;
  // Three.js cleanup
  if (_feedbackTorusMesh) teardownFeedback();
  if (_liquidMesh)        teardownLiquid();
  if (_stormMesh)         teardownStorm();
  if (_tunnelScene)       teardownTunnel();
  if (_threeRenderer) {
    window.removeEventListener('resize', _threeOnResize);
    _threeRenderer.dispose();
    if (_threeCanvas) { _threeCanvas.remove(); _threeCanvas = null; }
    _threeRenderer = null; _threeScene = null; _threeCamera = null;
    _threeStartTime = 0; _threeInitialized = false; _dummy = null;
  }
}

// ── Module-level reactive state store ─────────────────────────────────────────
// Lets VisualizerCanvas (ScoringPhase) and VisualizerControls (settings menu)
// share state without a React Context Provider.
let _state = { enabled: false, mode: 'bars', vortexSubMode: null };
const _subs = new Set();

function patchState(patch) {
  const prevMode = _state.mode;
  _state = { ..._state, ...patch };
  if (patch.mode !== undefined && patch.mode !== prevMode && _state.enabled) {
    switchMode(prevMode, patch.mode);
  }
  _subs.forEach((fn) => fn(_state));
}

export function useVisualizerState() {
  const [s, setS] = useState(_state);
  useEffect(() => {
    _subs.add(setS);
    return () => _subs.delete(setS);
  }, []);
  return s;
}

// ── Background style injection ─────────────────────────────────────────────────
// Makes .t-bg-page transparent so the z-index:-1 canvas shows behind the cards.
let _styleTag = null;

function injectBgStyle() {
  if (_styleTag) return;
  _styleTag = document.createElement('style');
  _styleTag.textContent = `
    html { background-color: #000 !important; }
    body { background-color: transparent !important; }
    .t-bg-page { background-color: transparent !important; }
  `;
  document.head.appendChild(_styleTag);
}

function removeBgStyle() {
  if (_styleTag) { _styleTag.remove(); _styleTag = null; }
}

// ── Shared audio graph builder ─────────────────────────────────────────────────
async function buildGraph(stream) {
  const audioCtx = new AudioContext();
  const source   = audioCtx.createMediaStreamSource(stream);

  // Full-spectrum analyser — used by all 2D modes (unchanged behaviour)
  const analyser = audioCtx.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.8;
  source.connect(analyser);

  // ── Frequency-separated paths for WebGL modes ──────────────────────────────
  // Bass: lowpass at 150 Hz
  const fBass = audioCtx.createBiquadFilter();
  fBass.type = 'lowpass'; fBass.frequency.value = 150;
  const aBass = audioCtx.createAnalyser();
  aBass.fftSize = 256; aBass.smoothingTimeConstant = 0.85;
  source.connect(fBass); fBass.connect(aBass);

  // Mids: bandpass at 1000 Hz (Q=1.0 — deliberately wide to catch broad signal)
  const fMids = audioCtx.createBiquadFilter();
  fMids.type = 'bandpass'; fMids.frequency.value = 1000; fMids.Q.value = 1.0;
  const aMids = audioCtx.createAnalyser();
  aMids.fftSize = 256; aMids.smoothingTimeConstant = 0.80;
  source.connect(fMids); fMids.connect(aMids);

  // Highs: highpass at 5000 Hz
  const fHighs = audioCtx.createBiquadFilter();
  fHighs.type = 'highpass'; fHighs.frequency.value = 5000;
  const aHighs = audioCtx.createAnalyser();
  aHighs.fftSize = 256; aHighs.smoothingTimeConstant = 0.70;
  source.connect(fHighs); fHighs.connect(aHighs);

  _stream   = stream;
  _audioCtx = audioCtx;
  _analyser = analyser;
  _analyserBass  = aBass;
  _analyserMids  = aMids;
  _analyserHighs = aHighs;
  // Allocate typed arrays once here — reused every frame inside getAudioData()
  _bassData  = new Uint8Array(aBass.frequencyBinCount);
  _midsData  = new Uint8Array(aMids.frequencyBinCount);
  _highsData = new Uint8Array(aHighs.frequencyBinCount);

  return analyser;
}

// ── Normalized audio data for WebGL draw functions ────────────────────────────
// Zero allocations — typed arrays are reused every call.
function getAudioData() {
  if (!_analyserBass) return { bass: 0, mids: 0, highs: 0 };
  _analyserBass.getByteFrequencyData(_bassData);
  _analyserMids.getByteFrequencyData(_midsData);
  _analyserHighs.getByteFrequencyData(_highsData);
  let bs = 0, ms = 0, hs = 0;
  for (let i = 0; i < _bassData.length;  i++) bs += _bassData[i];
  for (let i = 0; i < _midsData.length;  i++) ms += _midsData[i];
  for (let i = 0; i < _highsData.length; i++) hs += _highsData[i];
  return {
    bass:  Math.min(1, (bs / (_bassData.length  * 255)) * AUDIO_MULT.bass),
    mids:  Math.min(1, (ms / (_midsData.length  * 255)) * AUDIO_MULT.mids),
    highs: Math.min(1, (hs / (_highsData.length * 255)) * AUDIO_MULT.highs),
  };
}

// ── Shared helpers ─────────────────────────────────────────────────────────────
function avg(arr, start, end) {
  let sum = 0;
  for (let i = start; i < end; i++) sum += arr[i];
  return sum / (end - start);
}

// ── External stream activation — called from WebRTC context ───────────────────
// Activates the visualiser using an already-obtained MediaStream (e.g. the
// host's screen share audio received via WebRTC), bypassing the getDisplayMedia
// dialog entirely.
export async function activateFromExternalStream(stream) {
  if (!stream?.getAudioTracks().length) return false;
  await buildGraph(stream);
  // Don't register an 'ended' listener here — the WebRTC track lifecycle is
  // managed by WebRTCContext; it will stop sending when screen share ends.
  injectBgStyle();
  patchState({ enabled: true });
  return true;
}

// ── VisualizerControls — used inside the settings panel ───────────────────────
export function VisualizerControls({ onClose }) {
  const { t } = useTranslation();
  const { enabled, mode, vortexSubMode } = useVisualizerState();
  const { isHost, inPersonMode, isHostScreenSharing, hostAudioStream } = useVisualizerGameContext();
  const [showOptions, setShowOptions] = useState(false);
  const [error, setError]             = useState(null);

  // Auto-stop the visualiser if host stops screen sharing while it is running
  // (online mode only — in-person mode manages its own stream lifecycle).
  useEffect(() => {
    if (!inPersonMode && enabled && !isHostScreenSharing) {
      cleanupSingletons();
      removeBgStyle();
      patchState({ enabled: false });
    }
  }, [inPersonMode, enabled, isHostScreenSharing]);

  const hasHostAudio = !inPersonMode && !!hostAudioStream?.getAudioTracks().length;

  // In-person mode: anyone may share their own screen audio (original behaviour).
  // Online mode: audio always comes from the host's screen share via WebRTC.
  const showGetDisplayMedia = inPersonMode;

  if (!IS_SUPPORTED && !hasHostAudio && !showGetDisplayMedia) {
    return <p className="text-xs t-text-muted">{t('visualizer.notSupported')}</p>;
  }

  async function captureSystem() {
    setError(null);
    setShowOptions(false);
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ audio: true, video: false });
    } catch (e1) {
      if (e1.name === 'NotAllowedError') { setError(t('visualizer.permissionDenied')); return; }
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({ audio: true, video: true });
        stream.getVideoTracks().forEach((tr) => tr.stop());
      } catch (e2) {
        setError(e2.name === 'NotAllowedError' ? t('visualizer.permissionDenied') : t('visualizer.noAudio'));
        return;
      }
    }
    await activate(stream);
  }

  async function activateHostAudio() {
    setError(null);
    const ok = await activateFromExternalStream(hostAudioStream);
    if (!ok) {
      setError(t('visualizer.noAudio'));
      return;
    }
    onClose?.();
  }

  async function activate(stream) {
    if (!stream.getAudioTracks().length) {
      stream.getTracks().forEach((tr) => tr.stop());
      setError(t('visualizer.noAudio'));
      return;
    }
    await buildGraph(stream);
    injectBgStyle();
    stream.getTracks()[0].addEventListener('ended', () => {
      cleanupSingletons();
      removeBgStyle();
      patchState({ enabled: false });
    });
    patchState({ enabled: true });
    onClose?.();
    setTimeout(() => window.dispatchEvent(new Event('resize')), 400);
  }

  function stop() {
    cleanupSingletons();
    removeBgStyle();
    patchState({ enabled: false });
    setShowOptions(false);
  }

  if (enabled) {
    const modeBtnClass = (active) =>
      `text-xs px-2.5 py-1 rounded-full font-medium transition-colors t-focus-ring ${
        active ? 't-btn-primary' : 't-bg-input t-text-secondary hover:opacity-80'
      }`;

    return (
      <div className="space-y-2">
        <div role="radiogroup" aria-label={t('visualizer.modeLabel')} className="space-y-1.5">

          {/* Top row: Vortex (auto-cycle) + other main modes */}
          <div className="flex gap-1 flex-wrap">
            <button
              type="button" role="radio"
              aria-checked={mode === 'vortex' && vortexSubMode === null}
              onClick={() => { patchState({ mode: 'vortex', vortexSubMode: null }); vortexSetLockState(false); }}
              className={modeBtnClass(mode === 'vortex' && vortexSubMode === null)}
            >
              {t('visualizer.modeVortex')}
            </button>
            {['bars', 'wave', 'radial', 'particles', 'debug'].map((m) => (
              <button
                key={m} type="button" role="radio"
                aria-checked={mode === m}
                onClick={() => patchState({ mode: m, vortexSubMode: null })}
                className={modeBtnClass(mode === m)}
              >
                {t(`visualizer.mode${m.charAt(0).toUpperCase() + m.slice(1)}`)}
              </button>
            ))}
          </div>

          {/* Vortex sub-modes */}
          <div className="pt-1 border-t t-border">
            <p className="text-[10px] t-text-disabled mb-1">{t('visualizer.vortexSubLabel')}</p>
            <div className="flex gap-1 flex-wrap">
              {SUB_MODE_NAMES.map((name, i) => (
                <button
                  key={i} type="button" role="radio"
                  aria-checked={mode === 'vortex' && vortexSubMode === i}
                  onClick={() => vortexSelectSubMode(i)}
                  className={modeBtnClass(mode === 'vortex' && vortexSubMode === i)}
                >
                  {t(`visualizer.subMode${name}`)}
                </button>
              ))}
            </div>
          </div>

          {/* WebGL modes */}
          <div className="pt-1 border-t t-border">
            <p className="text-[10px] t-text-disabled mb-1">{t('visualizer.section.webgl')}</p>
            <div className="flex gap-1 flex-wrap">
              {(['feedback', 'liquid', 'storm', 'tunnel']).map((m) => (
                <button
                  key={m} type="button" role="radio"
                  aria-checked={mode === m}
                  onClick={() => patchState({ mode: m, vortexSubMode: null })}
                  className={modeBtnClass(mode === m)}
                >
                  {t(`visualizer.mode${m.charAt(0).toUpperCase() + m.slice(1)}`)}
                </button>
              ))}
            </div>
          </div>

        </div>
        <button
          type="button"
          onClick={stop}
          className="text-xs t-text-muted hover:t-text underline t-focus-ring rounded"
        >
          {t('visualizer.stop')}
        </button>
      </div>
    );
  }

  // ── Not yet enabled ────────────────────────────────────────────────────────

  // In-person mode: show the standard getDisplayMedia flow.
  if (showGetDisplayMedia) {
    if (showOptions) {
      return (
        <div className="space-y-2">
          <button
            type="button"
            onClick={captureSystem}
            className="w-full flex items-center gap-2 t-bg-input hover:opacity-80 t-border border t-text-secondary rounded-lg px-3 py-2 text-xs font-medium transition-colors t-focus-ring"
          >
            <span aria-hidden="true">🖥️</span> {t('visualizer.sourceSystem')}
          </button>
          <p className="text-xs t-text-disabled">{t('visualizer.systemHint')}</p>
          {error && <p role="alert" className="text-xs t-text-warning">{error}</p>}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setShowOptions(true)}
          className="w-full flex items-center justify-center gap-2 t-bg-input hover:opacity-80 t-border border t-text-secondary rounded-lg px-3 py-2.5 text-xs font-medium transition-colors t-focus-ring"
        >
          <span aria-hidden="true">🎵</span> {t('visualizer.enable')}
        </button>
        {error && <p role="alert" className="text-xs t-text-warning">{error}</p>}
      </div>
    );
  }

  // Online mode — host: must start screen sharing before visualiser can be enabled.
  if (isHost && !isHostScreenSharing) {
    return (
      <p className="text-xs t-text-muted">{t('visualizer.startScreenShareFirst')}</p>
    );
  }

  // Online mode — host with active screen share, or non-host with host audio available.
  if (hasHostAudio) {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={activateHostAudio}
          className="w-full flex items-center justify-center gap-2 t-bg-input hover:opacity-80 t-border border t-text-secondary rounded-lg px-3 py-2.5 text-xs font-medium transition-colors t-focus-ring"
        >
          <span aria-hidden="true">🎵</span> {t('visualizer.enableFromHost')}
        </button>
        {error && <p role="alert" className="text-xs t-text-warning">{error}</p>}
      </div>
    );
  }

  // Online mode — non-host, host not yet sharing.
  return (
    <p className="text-xs t-text-muted">{t('visualizer.waitingForHostScreen')}</p>
  );
}

// ── VisualizerDebugPanel — temporary testing overlay ─────────────────────────
// Mount in ScoringPhase alongside <VisualizerCanvas />. Only renders when the
// visualiser is enabled and vortex mode is selected.

const SUB_MODE_NAMES = ['Rings','Rays','Oscillate','Kaleido','Lava','Plasma','Spiral','Interfere','Starburst','Helix','Warp'];

// Initial slider values — read once at module load so useState stays stable
function _cfgToSv(cfg) {
  return {
    rotBase: cfg.rotBase, rotMusic: cfg.rotMusic, ringSpeed: cfg.baseSpeed,
    dramaT: cfg.dramaThresh, dramaC: cfg.dramaCooldown,
    minPeriod: cfg.subMinPeriod, maxPeriod: cfg.subMaxPeriod, fadeF: cfg.fadeFrames,
  };
}

export function VisualizerDebugPanel() {
  const { enabled, mode } = useVisualizerState();
  const [, tick]   = useState(0);   // drives re-renders for mode-highlight + slider refresh
  const [locked, setLocked] = useState(false);

  // Poll at 8 fps so the mode-highlight stays in sync with the animation
  useEffect(() => {
    if (!enabled || mode !== 'vortex') return;
    const id = setInterval(() => tick(n => n + 1), 125);
    return () => clearInterval(id);
  }, [enabled, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const curMode = VORTEX_STATE.current?.subMode ?? 0;
  const fadeDir = VORTEX_STATE.current?.fadeDir ?? 0;

  if (!enabled || mode !== 'vortex') return null;

  // Derive slider display values from module config on every render (no effect needed).
  // When a slider moves, vortexSetCfg writes the new value to VORTEX_MODE_CFG[curMode]
  // and tick() forces an immediate re-render so the slider reflects the change at once.
  const sv = _cfgToSv(VORTEX_MODE_CFG[curMode]);

  function jumpTo(i) { vortexJumpTo(i, locked); }
  function triggerNext() { vortexTriggerNext(); }
  function toggleLock() { const next = !locked; setLocked(next); vortexSetLockState(next); }

  // sl: label, displayed value, onChange(rawValue), min, max, step
  function sl(label, val, onChange, min, max, step) {
    const fmt = step < 0.001 ? 5 : step < 0.01 ? 4 : step < 1 ? 3 : 0;
    return (
      <div className="flex items-center gap-1.5" key={label}>
        <span className="w-24 text-right shrink-0" style={{ fontSize: 10, color: '#aaa' }}>{label}</span>
        <input type="range" min={min} max={max} step={step} value={val}
          onChange={e => { onChange(Number(e.target.value)); tick(n => n + 1); }}
          style={{ flex: 1, height: 3, accentColor: '#facc15' }}
        />
        <span style={{ width: 48, fontSize: 10, textAlign: 'right', color: '#facc15', fontFamily: 'monospace' }}>
          {val.toFixed(fmt)}
        </span>
      </div>
    );
  }

  // set: returns an onChange that writes the patched value to VORTEX config (saved per mode)
  function set(cfgPatch) { return v => vortexSetCfg(cfgPatch(v)); }

  const panelStyle = {
    position: 'fixed', top: 12, right: 12, zIndex: 9999, width: 300,
    background: 'rgba(0,0,0,0.88)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12, padding: '10px 12px', color: '#fff', pointerEvents: 'all',
    backdropFilter: 'blur(8px)', userSelect: 'none', fontSize: 11,
  };

  return createPortal(
    <div style={panelStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontWeight: 700, color: '#facc15', fontSize: 12 }}>Vortex Debug</span>
        <span style={{ color: '#aaa' }}>
          {SUB_MODE_NAMES[curMode]}{fadeDir !== 0 ? (fadeDir === -1 ? ' →fade out' : ' →fade in') : ''}
        </span>
      </div>

      {/* Sub-mode selector */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {SUB_MODE_NAMES.map((name, i) => (
          <button key={i} onClick={() => jumpTo(i)} style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 4, border: 'none', cursor: 'pointer',
            background: curMode === i ? '#facc15' : 'rgba(255,255,255,0.12)',
            color: curMode === i ? '#000' : 'rgba(255,255,255,0.75)',
            fontWeight: curMode === i ? 700 : 400,
          }}>
            {i}·{name}
          </button>
        ))}
      </div>

      {/* Cycle controls */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button onClick={toggleLock} style={{
          flex: 1, fontSize: 10, padding: '3px 0', borderRadius: 4, border: 'none', cursor: 'pointer',
          background: locked ? '#ef4444' : 'rgba(255,255,255,0.12)',
          color: locked ? '#fff' : 'rgba(255,255,255,0.75)',
        }}>
          {locked ? '⏸ Cycle locked' : '▶ Auto-cycle on'}
        </button>
        <button onClick={triggerNext} style={{
          flex: 1, fontSize: 10, padding: '3px 0', borderRadius: 4, border: 'none', cursor: 'pointer',
          background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.75)',
        }}>
          → Next mode
        </button>
      </div>

      {/* Variable sliders — settings are saved per mode */}
      <div style={{ marginBottom: 4, fontSize: 9, color: '#666' }}>Settings saved per mode</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sl('Rot speed',      sv.rotBase,          set(v => ({ rotBase:       v })),         0.0001, 0.02,  0.0001)}
        {sl('Rot+music',      sv.rotMusic,         set(v => ({ rotMusic:      v })),         0,      0.03,  0.001)}
        {sl('Ring speed',     sv.ringSpeed,        set(v => ({ baseSpeed:     v })),         0,      0.002, 0.00001)}
        {sl('Drama thr',      sv.dramaT,           set(v => ({ dramaThresh:   v })),         5,      200,   1)}
        {sl('Drama cd (s)',   sv.dramaC / 1000,    set(v => ({ dramaCooldown: v * 1000 })), 0.5,    30,    0.5)}
        {sl('Min period (s)', sv.minPeriod / 1000, set(v => ({ subMinPeriod:  v * 1000 })), 1,      30,    0.5)}
        {sl('Max period (s)', sv.maxPeriod / 1000, set(v => ({ subMaxPeriod:  v * 1000 })), 5,      60,    0.5)}
        {sl('Fade frames',    sv.fadeF,            set(v => ({ fadeFrames:    v })),         2,      60,    1)}
      </div>
    </div>,
    document.body
  );
}

// ── VisualizerCanvas — mounted by ScoringPhase, renders the full-screen canvas ─
export default function VisualizerCanvas() {
  const { enabled, mode } = useVisualizerState();

  const canvasRef    = useRef(null);
  const animFrameRef = useRef(null);
  const dimRef       = useRef({ w: window.innerWidth, h: window.innerHeight });
  const modeRef      = useRef(mode);
  const particlesRef = useRef([]);
  const peakHoldRef  = useRef([]);
  const hueRef       = useRef(0);
  useEffect(() => {
    modeRef.current = mode;
    if (mode !== 'vortex') vortexReset(); // reset when leaving vortex
  }, [mode]);

  // Declare animation helpers before the useEffects that reference them.
  function cancelAnim() {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }

  function startLoop(analyser) {
    const freqData = new Uint8Array(analyser.frequencyBinCount);
    const timeData = new Uint8Array(analyser.fftSize);
    function loop() {
      animFrameRef.current = requestAnimationFrame(loop);
      if (!_analyser) return;
      const m = modeRef.current;
      // ── WebGL path — delegate to Three.js renderers ──────────────────────
      if (WEBGL_MODES.includes(m)) {
        const audio   = getAudioData();
        const elapsed = (performance.now() - _threeStartTime) / 1000;
        if (m === 'feedback') drawFeedback(audio);
        else if (m === 'liquid') drawLiquid(audio, elapsed);
        else if (m === 'storm')  drawStorm(audio, elapsed);
        else if (m === 'tunnel') drawTunnel(audio, elapsed);
        return;
      }
      // ── 2D canvas path — unchanged ────────────────────────────────────────
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const { w, h } = dimRef.current;
      hueRef.current = (hueRef.current + 0.3) % 360;
      switch (m) {
        case 'bars':      drawBars(ctx, freqData, w, h);      break;
        case 'wave':      drawWave(ctx, timeData, w, h);      break;
        case 'radial':    drawRadial(ctx, freqData, w, h);    break;
        case 'particles': drawParticles(ctx, freqData, w, h); break;
        case 'vortex':    drawVortex(ctx, freqData, w, h);    break;
        case 'debug':     drawDebug(ctx, w, h);               break;
      }
    }
    loop();
  }

  // Auto-start if the audio graph is already alive (carried from a previous round)
  useEffect(() => {
    if (_analyser) {
      peakHoldRef.current = new Array(_analyser.frequencyBinCount).fill(0);
      if (!_state.enabled) patchState({ enabled: true }); // sync state if stale
      injectBgStyle();
      startLoop(_analyser);
    }
    return () => {
      cancelAnim();
      removeBgStyle();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // React when enabled changes (e.g., user stops from settings while in scoring)
  useEffect(() => {
    if (!enabled) {
      cancelAnim();
      removeBgStyle();
      particlesRef.current = [];
      peakHoldRef.current  = [];
    } else if (_analyser && !animFrameRef.current) {
      peakHoldRef.current = new Array(_analyser.frequencyBinCount).fill(0);
      injectBgStyle();
      startLoop(_analyser);
    }
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync canvas size to the full viewport
  useEffect(() => {
    if (!enabled) return;
    function updateSize() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      dimRef.current = { w: window.innerWidth, h: window.innerHeight };
    }
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [enabled]);

  // ── Drawing ──────────────────────────────────────────────────────────────────

  function drawBars(ctx, freqData, w, h) {
    _analyser.getByteFrequencyData(freqData);
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, w, h);
    const barCount = Math.min(freqData.length, 120);
    const gap = 3, barW = Math.max(1, (w - gap * barCount) / barCount);
    const base = h * 0.72, peak = peakHoldRef.current;
    for (let i = 0; i < barCount; i++) {
      const val  = freqData[Math.floor((i / barCount) * freqData.length)];
      const barH = (val / 255) * (h * 0.68);
      const x    = i * (barW + gap);
      const grad = ctx.createLinearGradient(0, base - barH, 0, base);
      grad.addColorStop(0,   `hsl(${(i / barCount) * 60 + 180}, 100%, 85%)`);
      grad.addColorStop(0.5, `hsl(${(i / barCount) * 60 + 220}, 90%, 60%)`);
      grad.addColorStop(1,   `hsl(260, 80%, 28%)`);
      ctx.fillStyle = grad;
      ctx.fillRect(x, base - barH, barW, barH);
      ctx.globalAlpha = 0.18;
      ctx.fillRect(x, base, barW, barH * 0.38);
      ctx.globalAlpha = 1;
      if (barH > peak[i]) { peak[i] = barH; } else { peak[i] = Math.max(0, peak[i] - 1.4); }
      if (peak[i] > 3) {
        ctx.fillStyle = `hsl(${(i / barCount) * 60 + 185}, 100%, 95%)`;
        ctx.fillRect(x, base - peak[i] - 2, barW, 2);
      }
    }
  }

  function drawWave(ctx, timeData, w, h) {
    _analyser.getByteTimeDomainData(timeData);
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, w, h);
    const mid = h / 2, hue = hueRef.current, sliceW = w / timeData.length;
    for (const { lw, alpha } of [{ lw: 14, alpha: 0.08 }, { lw: 5, alpha: 0.32 }, { lw: 2, alpha: 1 }]) {
      ctx.beginPath();
      ctx.strokeStyle = `hsla(${hue}, 100%, 70%, ${alpha})`;
      ctx.lineWidth = lw; ctx.lineJoin = 'round';
      let x = 0;
      for (let i = 0; i < timeData.length; i++) {
        const y = mid + ((timeData[i] / 128) - 1) * (h * 0.42);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        x += sliceW;
      }
      ctx.stroke();
    }
  }

  function drawRadial(ctx, freqData, w, h) {
    _analyser.getByteFrequencyData(freqData);
    ctx.fillStyle = 'black'; ctx.fillRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2, radius = Math.min(w, h) * 0.18;
    const binCount = Math.min(freqData.length, 128), hue = hueRef.current;
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < binCount; i++) {
        const barLen = (freqData[i] / 255) * radius * 1.7;
        const angle  = (i / binCount) * Math.PI * 2 + pass * Math.PI;
        ctx.beginPath();
        ctx.strokeStyle = `hsl(${(hue + (i / binCount) * 140) % 360}, 100%, 72%)`;
        ctx.lineWidth = 2.5;
        ctx.moveTo(cx + Math.cos(angle) * radius,           cy + Math.sin(angle) * radius);
        ctx.lineTo(cx + Math.cos(angle) * (radius + barLen), cy + Math.sin(angle) * (radius + barLen));
        ctx.stroke();
      }
    }
    const pulse = radius * 0.12 + (avg(freqData, 0, 16) / 255) * radius * 0.15;
    ctx.beginPath(); ctx.arc(cx, cy, pulse, 0, Math.PI * 2);
    ctx.fillStyle = `hsl(${hue}, 80%, 40%)`; ctx.fill();
  }

  function drawParticles(ctx, freqData, w, h) {
    _analyser.getByteFrequencyData(freqData);
    const bassAvg = avg(freqData, 0, 8), midAvg = avg(freqData, 8, 64), highAvg = avg(freqData, 64, 128);
    const overall = (bassAvg + midAvg + highAvg) / 3;
    ctx.fillStyle = 'rgba(0,0,0,0.2)'; ctx.fillRect(0, 0, w, h);
    const particles = particlesRef.current;
    if (overall > 50 && particles.length < 350) {
      const count = Math.max(1, Math.floor((overall / 255) * 12));
      for (let i = 0; i < count; i++) {
        const dominant = bassAvg >= midAvg && bassAvg >= highAvg ? 'bass' : midAvg >= highAvg ? 'mid' : 'high';
        const hue = dominant === 'bass' ? 20 : dominant === 'mid' ? 130 : 260;
        const maxLife = 90 + Math.random() * 50;
        particles.push({ x: w/2+(Math.random()-.5)*w*.8, y: h/2+(Math.random()-.5)*h*.8,
          vx: (Math.random()-.5)*(overall/35), vy: -(Math.random()*(overall/55)+.5),
          life: maxLife, maxLife, hue, size: 2.5+(overall/90) });
      }
    }
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.x += p.vx; p.y += p.vy; p.life--;
      if (p.life <= 0) { particles.splice(i, 1); continue; }
      ctx.globalAlpha = p.life / p.maxLife;
      ctx.fillStyle = `hsl(${p.hue}, 100%, 65%)`;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  function drawDebug(ctx, w, h) {
    if (!_analyserBass) return;
    // getAudioData() populates the typed arrays as a side effect — read raw
    // sums from them immediately after, before the next frame overwrites them.
    const audio = getAudioData(); // multiplied + capped values (what WebGL sees)
    let bs = 0, ms = 0, hs = 0;
    for (let i = 0; i < _bassData.length;  i++) bs += _bassData[i];
    for (let i = 0; i < _midsData.length;  i++) ms += _midsData[i];
    for (let i = 0; i < _highsData.length; i++) hs += _highsData[i];
    const raw = {
      bass:  bs / (_bassData.length  * 255),
      mids:  ms / (_midsData.length  * 255),
      highs: hs / (_highsData.length * 255),
    };

    _debugMax.bass  = Math.max(_debugMax.bass,  audio.bass);
    _debugMax.mids  = Math.max(_debugMax.mids,  audio.mids);
    _debugMax.highs = Math.max(_debugMax.highs, audio.highs);
    _debugSum.bass  += audio.bass;
    _debugSum.mids  += audio.mids;
    _debugSum.highs += audio.highs;
    _debugSum.count += 1;

    const n = _debugSum.count;
    const channels = [
      { label: 'Bass',  mult: AUDIO_MULT.bass,  raw: raw.bass,  value: audio.bass,  max: _debugMax.bass,  avg: _debugSum.bass  / n, hue: 10  },
      { label: 'Mids',  mult: AUDIO_MULT.mids,  raw: raw.mids,  value: audio.mids,  max: _debugMax.mids,  avg: _debugSum.mids  / n, hue: 130 },
      { label: 'Highs', mult: AUDIO_MULT.highs, raw: raw.highs, value: audio.highs, max: _debugMax.highs, avg: _debugSum.highs / n, hue: 220 },
    ];

    ctx.clearRect(0, 0, w, h);
    const barW      = Math.min(120, (w - 80 - 30 * 2) / 3);
    const maxH      = h * 0.6;
    const startX    = (w - (barW * 3 + 30 * 2)) / 2;
    const baseY     = h * 0.75;
    const labelSize = Math.max(11, Math.min(16, w / 40));
    const numSize   = Math.max(10, Math.min(14, w / 48));

    channels.forEach(({ label, mult, raw, value, max, avg, hue }, i) => {
      const x    = startX + i * (barW + 30);
      const barH = value * maxH;
      const maxY = baseY - max * maxH;
      const avgY = baseY - avg * maxH;

      // Background track
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      ctx.beginPath();
      ctx.roundRect(x, baseY - maxH, barW, maxH, 6);
      ctx.fill();

      // Live bar
      const grad = ctx.createLinearGradient(0, baseY, 0, baseY - maxH);
      grad.addColorStop(0, `hsla(${hue},100%,50%,0.9)`);
      grad.addColorStop(1, `hsla(${hue + 40},100%,70%,0.6)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x, baseY - barH, barW, barH, [0, 0, 6, 6]);
      ctx.fill();

      // Max marker line (dashed)
      ctx.strokeStyle = `hsla(${hue},100%,80%,0.85)`;
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(x, maxY);
      ctx.lineTo(x + barW, maxY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Average marker line (solid, dimmer)
      ctx.strokeStyle = `hsla(${hue},60%,70%,0.55)`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x, avgY);
      ctx.lineTo(x + barW, avgY);
      ctx.stroke();

      // Channel label + multiplier
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = `bold ${labelSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`${label} ×${mult}`, x + barW / 2, baseY + labelSize + 6);

      // Current value (multiplied — what WebGL sees)
      ctx.fillStyle = `hsl(${hue},100%,75%)`;
      ctx.font = `${numSize}px monospace`;
      ctx.fillText(value.toFixed(3), x + barW / 2, baseY + labelSize + 6 + numSize + 4);

      // Raw value (pre-multiplier)
      ctx.fillStyle = 'rgba(255,255,255,0.38)';
      ctx.font = `${numSize}px monospace`;
      ctx.fillText(`raw ${raw.toFixed(3)}`, x + barW / 2, baseY + labelSize + 6 + numSize * 2 + 8);

      // Avg value (multiplied)
      ctx.fillStyle = `hsla(${hue},60%,65%,0.7)`;
      ctx.font = `${numSize}px monospace`;
      ctx.fillText(`avg ${avg.toFixed(3)}`, x + barW / 2, baseY + labelSize + 6 + numSize * 3 + 12);

      // Max value (above the max line)
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.font = `${numSize}px monospace`;
      ctx.fillText(`max ${max.toFixed(3)}`, x + barW / 2, baseY - max * maxH - 8);
    });
  }

  // ── Glow-stroke helper — 3 passes on the current path: halo / mid / bright core
  // Call after beginPath + arc/lineTo but before the next beginPath.
  function glow3(ctx2, hue, alpha, bw) {
    ctx2.lineWidth = bw * 6;    ctx2.strokeStyle = `hsla(${hue},90%,62%,${alpha * 0.07})`;  ctx2.stroke();
    ctx2.lineWidth = bw * 2;    ctx2.strokeStyle = `hsla(${hue},100%,78%,${alpha * 0.30})`; ctx2.stroke();
    ctx2.lineWidth = bw * 0.55; ctx2.strokeStyle = `hsla(${hue},100%,98%,${alpha * 0.88})`; ctx2.stroke();
  }

  function drawVortex(ctx, freqData, w, h) {
    _analyser.getByteFrequencyData(freqData);

    // ── Lazy-init ────────────────────────────────────────────────────────────────
    if (!VORTEX_STATE.current) {
      const now0      = performance.now();
      const p0        = VORTEX_CFG.subMinPeriod + Math.random() * (VORTEX_CFG.subMaxPeriod - VORTEX_CFG.subMinPeriod);
      const startSub  = _state.vortexSubMode ?? 0;
      const startLock = _state.vortexSubMode !== null;
      if (startLock) vortexLoadModeCfg(startSub);
      VORTEX_STATE.current = {
        angle: 0, hueBase: Math.random() * 360,
        rings: Array.from({ length: VORTEX_RING_COUNT }, (_, i) => i / VORTEX_RING_COUNT),
        prevEnergy: 0, lastDramaAt: 0, flashFrames: 0,
        subMode: startSub, pendingMode: (startSub + 1) % VORTEX_SUB_COUNT, subAlpha: 1, fadeDir: 0,
        nextSwitchAt: now0 + (startLock ? 1e9 : p0), subModeStartAt: now0,
      };
    }
    const vd = VORTEX_STATE.current;

    // ── Audio analysis ────────────────────────────────────────────────────────────
    const n      = freqData.length;
    const bass   = avg(freqData, 0,                    Math.max(1, Math.floor(n * 0.05)));
    const mid    = avg(freqData, Math.floor(n * 0.05), Math.floor(n * 0.30));
    const high   = avg(freqData, Math.floor(n * 0.30), Math.floor(n * 0.65));
    const energy = bass * 0.55 + mid * 0.30 + high * 0.15;

    // ── Drama — instant palette shift on a sudden energy spike ───────────────────
    const now      = performance.now();
    const t        = now * 0.001;
    const jump     = energy - vd.prevEnergy;
    const isDrama  = jump > VORTEX_CFG.dramaThresh && now - vd.lastDramaAt > VORTEX_CFG.dramaCooldown;
    if (isDrama) { vd.hueBase = Math.random() * 360; vd.lastDramaAt = now; vd.flashFrames = VORTEX_DRAMA_FLASH; }
    vd.prevEnergy = energy * 0.55 + vd.prevEnergy * 0.45;
    if (vd.flashFrames > 0) vd.flashFrames--;
    const flash = vd.flashFrames / VORTEX_DRAMA_FLASH;

    // ── Sub-mode fade state machine ───────────────────────────────────────────────
    const FADE_STEP = 1 / VORTEX_CFG.fadeFrames;
    if (vd.fadeDir === -1) {
      vd.subAlpha -= FADE_STEP;
      if (vd.subAlpha <= 0) {
        vd.subAlpha = 0; vd.subMode = vd.pendingMode; vd.fadeDir = 1;
        vortexLoadModeCfg(vd.subMode);
        vd.subModeStartAt = now;
        vd.nextSwitchAt   = now + VORTEX_CFG.subMinPeriod + Math.random() * (VORTEX_CFG.subMaxPeriod - VORTEX_CFG.subMinPeriod);
      }
    } else if (vd.fadeDir === 1) {
      vd.subAlpha += FADE_STEP;
      if (vd.subAlpha >= 1) { vd.subAlpha = 1; vd.fadeDir = 0; }
    } else if (_state.vortexSubMode === null) {
      // Stable — trigger a switch on schedule or on a drama event after min dwell time
      // Only when no specific sub-mode is locked; locked modes never auto-cycle.
      const earlyOk = isDrama && (now - vd.subModeStartAt) > VORTEX_CFG.subMinPeriod;
      if (now >= vd.nextSwitchAt || earlyOk) {
        vd.pendingMode = (vd.subMode + 1 + Math.floor(Math.random() * (VORTEX_SUB_COUNT - 1))) % VORTEX_SUB_COUNT;
        vd.fadeDir     = -1;
      }
    }

    // ── Shared per-frame updates ──────────────────────────────────────────────────
    vd.angle += VORTEX_CFG.rotBase + (mid / 255) * VORTEX_CFG.rotMusic;
    const speed = VORTEX_CFG.baseSpeed + (energy / 255) * VORTEX_CFG.speedRange;
    const rings  = vd.rings;
    for (let i = 0; i < rings.length; i++) { rings[i] += speed; if (rings[i] >= 1) rings[i] -= 1; }

    const cx = w / 2, cy = h / 2;
    // maxR extends beyond every screen corner so the outermost ring is always clipped
    const maxR = Math.sqrt(w * w + h * h) * 0.55;
    const hue0 = vd.hueBase;

    // ── Clear ────────────────────────────────────────────────────────────────────
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);

    // ── Centre glow — always full alpha, shared across all sub-modes ──────────────
    const glowR = 10 + (bass / 255) * 24 + flash * 28;
    const glowA = 0.13 + (energy / 255) * 0.15 + flash * 0.30;
    const gg = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR * 5);
    gg.addColorStop(0,    `hsla(${hue0},65%,96%,${glowA})`);
    gg.addColorStop(0.40, `hsla(${hue0},70%,62%,${glowA * 0.40})`);
    gg.addColorStop(1,    'rgba(0,0,0,0)');
    ctx.fillStyle = gg;
    ctx.beginPath(); ctx.arc(cx, cy, glowR * 5, 0, Math.PI * 2); ctx.fill();

    // ── Sub-mode content — fades in/out via globalAlpha ───────────────────────────
    ctx.save();
    ctx.globalAlpha = vd.subAlpha;

    // Rings sorted ascending z (back to front), reused by multiple sub-modes
    const order = Array.from({ length: rings.length }, (_, i) => i).sort((a, b) => rings[a] - rings[b]);

    switch (vd.subMode) {

      // ── 0: Tunnel rings with helical arc segments ──────────────────────────────
      case 0: {
        const segArc = Math.PI * 2 * 0.70 / 3;
        const gapArc = (Math.PI * 2 - segArc * 3) / 3;
        for (const i of order) {
          const z = rings[i], ze = z * z * (3 - 2 * z);
          if (ze < 0.01) continue;
          const r = maxR * ze; if (r < 2) continue;
          const alpha   = Math.min(0.9, ze * 2.1 + flash * 0.4) * (0.30 + (energy / 255) * 0.55);
          const ringHue = ((hue0 + (i / rings.length) * 240 + Math.sin(t * 0.7 + i * 1.15) * 28) % 360 + 360) % 360;
          const bw      = 0.5 + ze * 2.5 + (bass / 255) * 2 * ze;
          const rAngle  = vd.angle + i * 0.28;
          for (let s = 0; s < 3; s++) {
            const a0 = rAngle + s * (segArc + gapArc);
            ctx.beginPath(); ctx.arc(cx, cy, r, a0, a0 + segArc);
            glow3(ctx, (ringHue + s * 18) % 360, alpha, bw);
          }
        }
        break;
      }

      // ── 1: Radial rays with soft glowing pulses travelling outward ────────────
      case 1: {
        const RAY = 12;
        for (let i = 0; i < RAY; i++) {
          const angle  = vd.angle * 0.6 + (i / RAY) * Math.PI * 2;
          const rayHue = ((hue0 + (i / RAY) * 180) % 360 + 360) % 360;
          const fade   = (Math.sin(t * 0.5 + i * 0.85) * 0.5 + 0.5) * 0.65 + 0.10;
          const alpha  = fade * (0.35 + (energy / 255) * 0.50);
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.lineTo(cx + Math.cos(angle) * maxR, cy + Math.sin(angle) * maxR);
          glow3(ctx, rayHue, alpha * 0.65, 1.2 + (mid / 255) * 1.5);
          for (let p = 0; p < 3; p++) {
            const ph  = ((t * 0.35 + p / 3 + i * 0.13) % 1);
            const pr  = ph * maxR;
            const px  = cx + Math.cos(angle) * pr, py = cy + Math.sin(angle) * pr;
            const ps  = (2 + (energy / 255) * 6) * (1 - ph * 0.5);
            const pa  = alpha * (1 - ph * 0.55) * (0.45 + (energy / 255) * 0.40);
            ctx.beginPath(); ctx.arc(px, py, ps * 2.5, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${rayHue},90%,65%,${pa * 0.15})`; ctx.fill();
            ctx.beginPath(); ctx.arc(px, py, ps * 0.7, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${rayHue},100%,98%,${pa * 0.75})`; ctx.fill();
          }
        }
        break;
      }

      // ── 2: Oscillating rings — extreme waveform, single vivid hue ─────────────
      case 2: {
        const WPTS = 96;
        for (const i of order) {
          const z = rings[i], ze = z * z * (3 - 2 * z);
          if (ze < 0.01) continue;
          const baseR = maxR * ze; if (baseR < 2) continue;
          const alpha = Math.min(0.9, ze * 2.2) * (0.40 + (energy / 255) * 0.50);
          const bw    = 0.5 + ze * 2.5;
          const amp1  = (bass / 255) * baseR * 0.22, sp1 = 4 + i % 3;
          const amp2  = (mid  / 255) * baseR * 0.14, sp2 = 7 + i % 4;
          ctx.beginPath();
          for (let p = 0; p <= WPTS; p++) {
            const a   = (p / WPTS) * Math.PI * 2;
            const osc = Math.sin(a * sp1 + vd.angle * 2.5 + i * 0.6) * amp1
                      + Math.sin(a * sp2 - vd.angle * 1.8 + i * 1.2) * amp2;
            const r2  = Math.max(1, baseR + osc);
            const px  = cx + Math.cos(a) * r2, py = cy + Math.sin(a) * r2;
            p === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
          }
          ctx.closePath();
          glow3(ctx, hue0, alpha, bw);
        }
        break;
      }

      // ── 3: Kaleidoscope — 8-fold radial symmetry ──────────────────────────────
      case 3: {
        const FOLD = 8, SEG_PF = 5;
        for (let f = 0; f < FOLD; f++) {
          const fAngle = (f / FOLD) * Math.PI * 2 + vd.angle;
          for (let s = 0; s < SEG_PF; s++) {
            const frac = (s + 1) / (SEG_PF + 1);
            const ze   = frac * frac * (3 - 2 * frac);
            const r1   = maxR * ze * 0.82, r2 = maxR * Math.min(1.06, ze + 0.18);
            const sw   = Math.sin(t * 0.4 + s * 0.9 + f * 0.3) * 0.45;
            const a    = fAngle + sw;
            const kH   = ((hue0 + f * (360 / FOLD) + s * 28) % 360 + 360) % 360;
            const ka   = ze * (0.45 + (energy / 255) * 0.45);
            ctx.beginPath();
            ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
            ctx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2);
            glow3(ctx, kH, ka, 0.6 + ze * 2);
          }
        }
        break;
      }

      // ── 4: Lava lamp — soft orbiting luminous blobs ───────────────────────────
      case 4: {
        const BLOBS = 6, bMax = Math.min(w, h) * 0.42;
        for (let b = 0; b < BLOBS; b++) {
          const oA = vd.angle * (0.25 + (b % 3) * 0.12) + (b / BLOBS) * Math.PI * 2;
          const oR = bMax * (0.18 + (b / BLOBS) * 0.55);
          const bx = cx + Math.cos(oA) * oR, by = cy + Math.sin(oA) * oR;
          const bR = bMax * (0.10 + Math.sin(t * 0.4 + b * 1.3) * 0.04) * (1 + (energy / 255) * 0.5);
          const bH = ((hue0 + b * 38 + t * 4) % 360 + 360) % 360;
          const bA = 0.25 + (energy / 255) * 0.25;
          const bg = ctx.createRadialGradient(bx, by, 0, bx, by, bR * 2.5);
          bg.addColorStop(0,   `hsla(${bH},85%,68%,${bA + 0.15})`);
          bg.addColorStop(0.5, `hsla(${bH},80%,45%,${bA * 0.5})`);
          bg.addColorStop(1,   'rgba(0,0,0,0)');
          ctx.fillStyle = bg;
          ctx.beginPath(); ctx.arc(bx, by, bR * 2.5, 0, Math.PI * 2); ctx.fill();
        }
        break;
      }

      // ── 5: Plasma ball — branching tendrils with organic motion ───────────────
      case 5: {
        const sphR  = Math.min(w, h) * 1;
        const bassN = bass   / 255;
        const midN  = mid    / 255;
        const highN = high   / 255;

        // Additive blending makes overlapping glows bloom naturally
        ctx.globalCompositeOperation = 'lighter';
        ctx.lineCap  = 'round';
        ctx.lineJoin = 'round';

        // Sphere boundary glow
        const sGrad = ctx.createRadialGradient(cx, cy, sphR * 0.78, cx, cy, sphR * 1.22);
        sGrad.addColorStop(0,    `hsla(270,100%,58%,${0.06 + bassN * 0.07})`);
        sGrad.addColorStop(0.45, `hsla(280,90%,52%,${0.12 + bassN * 0.10})`);
        sGrad.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.fillStyle = sGrad;
        ctx.beginPath(); ctx.arc(cx, cy, sphR * 1.22, 0, Math.PI * 2); ctx.fill();

        ctx.beginPath(); ctx.arc(cx, cy, sphR, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(270,80%,76%,${0.10 + bassN * 0.12})`;
        ctx.lineWidth   = 1.2 + bassN;
        ctx.stroke();

        // White / pink core radial gradient
        const coreR = 6 + bassN * 14 + flash * 16;
        const cGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR * 4);
        cGrad.addColorStop(0,    `rgba(255,255,255,${0.85 + bassN * 0.14})`);
        cGrad.addColorStop(0.12, `hsla(330,100%,92%,0.68)`);
        cGrad.addColorStop(0.42, `hsla(300,90%,64%,0.28)`);
        cGrad.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.fillStyle = cGrad;
        ctx.beginPath(); ctx.arc(cx, cy, coreR * 4, 0, Math.PI * 2); ctx.fill();

        // Tendrils — count scales with high-freq (more branching when treble is active)
        const TEND   = Math.floor(8 + highN * 8);   // 8–16
        const SEGS   = 12;
        const tLen   = sphR * (0.88 + bassN * 0.25);
        const segLen = tLen / SEGS;
        const baseW  = 1.0 + bassN * 2.0;

        for (let ti = 0; ti < TEND; ti++) {
          // Each tendril drifts with an independent phase offset (organic motion)
          const phase = t * (0.7 + (ti % 4) * 0.25) + ti * 2.399;
          const baseA = (ti / TEND) * Math.PI * 2
                      + vd.angle * 0.06
                      + Math.sin(t * 0.25 + ti * 0.83) * 0.45;

          // Build vertex list — wiggle amplitude grows toward the tip
          const pts = [{ x: cx, y: cy }];
          let px = cx, py = cy, pa = baseA;
          for (let s = 0; s < SEGS; s++) {
            const frac = (s + 1) / SEGS;
            const wg   = Math.sin(phase * 2.2 + s * 1.6) * 0.30 * (0.5 + frac)
                       + Math.sin(phase * 3.9 + s * 2.7) * 0.15 * frac;
            pa += wg * (1 + midN * 0.3);
            px += Math.cos(pa) * segLen;
            py += Math.sin(pa) * segLen;
            pts.push({ x: px, y: py });
          }

          // Per-segment colour: white/pink (core) → blue-purple → magenta (tip)
          for (let s = 0; s < pts.length - 1; s++) {
            const frac = s / Math.max(1, pts.length - 2);
            let segHue;
            if (frac < 0.35)      segHue = 320 - frac * (60 / 0.35);
            else if (frac < 0.70) segHue = 260 - (frac - 0.35) * (20 / 0.35);
            else                  segHue = 240 + (frac - 0.70) * (70 / 0.30);
            const segL = 65 + (1 - frac) * 25;
            const segA = (0.55 + bassN * 0.25) * (1 - frac * 0.42);
            const sw   = Math.max(0.3, baseW * (1 - frac * 0.60));

            ctx.beginPath();
            ctx.moveTo(pts[s].x, pts[s].y);
            ctx.lineTo(pts[s + 1].x, pts[s + 1].y);
            ctx.lineWidth   = sw * 5;
            ctx.strokeStyle = `hsla(${segHue},90%,${segL}%,${segA * 0.08})`;
            ctx.stroke();
            ctx.lineWidth   = sw;
            ctx.strokeStyle = `hsla(${segHue},100%,${Math.min(100, segL + 18)}%,${segA * 0.75})`;
            ctx.stroke();
          }

          // Glowing endpoint node
          const tip  = pts[pts.length - 1];
          const tipD = Math.hypot(tip.x - cx, tip.y - cy);
          if (tipD > 4) {
            const tipA = (0.38 + bassN * 0.28) * Math.min(1, tipD / (sphR * 0.3));
            ctx.beginPath(); ctx.arc(tip.x, tip.y, 3 + bassN * 3, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(300,100%,80%,${tipA * 0.22})`; ctx.fill();
            ctx.beginPath(); ctx.arc(tip.x, tip.y, 1.2 + bassN, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255,220,255,${tipA * 0.85})`; ctx.fill();
          }

          // Sub-branch — driven by high-freq energy, forks off mid-tendril
          if (highN > 0.20) {
            const brIdx  = Math.min(Math.floor(pts.length * (0.30 + (ti % 3) * 0.14)), pts.length - 1);
            const bBase  = pts[brIdx];
            const bLen   = tLen * (0.15 + highN * 0.25);
            const bAngle = pa - 1.1 * (ti % 2 === 0 ? 1 : -1);
            const bTipX  = bBase.x + Math.cos(bAngle) * bLen;
            const bTipY  = bBase.y + Math.sin(bAngle) * bLen;
            ctx.beginPath();
            ctx.moveTo(bBase.x, bBase.y);
            ctx.lineTo(bTipX, bTipY);
            ctx.lineWidth   = baseW * 0.35 + highN * 0.4;
            ctx.strokeStyle = `hsla(270,100%,78%,${0.22 + highN * 0.22})`;
            ctx.stroke();
            ctx.beginPath(); ctx.arc(bTipX, bTipY, 0.8 + highN * 1.2, 0, Math.PI * 2);
            ctx.fillStyle   = `rgba(230,180,255,${0.45 + highN * 0.30})`; ctx.fill();
          }
        }

        ctx.globalCompositeOperation = 'source-over';
        break;
      }

      // ── 6: Spiral arms — rotating galaxy ──────────────────────────────────────
      case 6: {
        const ARMS = 3, STEPS = 110;
        for (let arm = 0; arm < ARMS; arm++) {
          const armH = ((hue0 + arm * 45) % 360 + 360) % 360;
          ctx.beginPath();
          for (let s = 0; s <= STEPS; s++) {
            const frac  = s / STEPS;
            const theta = frac * Math.PI * 3.8 + (arm / ARMS) * Math.PI * 2 + vd.angle * 0.35;
            const r     = maxR * frac * (1 + Math.sin(t * 0.6 + arm * 1.2) * 0.06) * 1.05;
            const px    = cx + Math.cos(theta) * r, py = cy + Math.sin(theta) * r;
            s === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
          }
          glow3(ctx, armH, 0.35 + (energy / 255) * 0.45, 0.8 + (energy / 255) * 1.0);
        }
        break;
      }

      // ── 7: Interference — two orbiting ring sources ────────────────────────────
      case 7: {
        const iorb = Math.min(w, h) * 0.07 + (mid / 255) * Math.min(w, h) * 0.05;
        const s1x  = cx + Math.cos(vd.angle * 0.65) * iorb;
        const s1y  = cy + Math.sin(vd.angle * 0.65) * iorb;
        const s2x  = cx - Math.cos(vd.angle * 0.65) * iorb;
        const s2y  = cy - Math.sin(vd.angle * 0.65) * iorb;
        for (const i of order) {
          const ze = rings[i] * rings[i] * (3 - 2 * rings[i]);
          if (ze < 0.01) continue;
          const r  = maxR * ze; if (r < 2) continue;
          const iH = ((hue0 + i * 22) % 360 + 360) % 360;
          const ia = Math.min(0.80, ze * 2) * (0.28 + (energy / 255) * 0.45);
          const bw = 0.5 + ze * 2;
          ctx.beginPath(); ctx.arc(s1x, s1y, r, 0, Math.PI * 2); glow3(ctx, iH, ia, bw);
          ctx.beginPath(); ctx.arc(s2x, s2y, r, 0, Math.PI * 2); glow3(ctx, (iH + 130) % 360, ia * 0.85, bw);
        }
        break;
      }

      // ── 8: Starburst — 24 radial spokes pulsing with beat ─────────────────────
      case 8: {
        const SPOKES = 24;
        for (let i = 0; i < SPOKES; i++) {
          const a     = (i / SPOKES) * Math.PI * 2 + vd.angle * 0.22;
          const isMaj = i % 4 === 0;
          const beat  = isMaj ? bass / 255 : mid / 255;
          const endR  = maxR * (isMaj ? 1.05 : 0.52 + beat * 0.32);
          const startR = maxR * 0.02;
          const sH    = ((hue0 + (i / SPOKES) * 130) % 360 + 360) % 360;
          const sa    = (isMaj ? 0.55 : 0.22) + beat * 0.30;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a) * startR, cy + Math.sin(a) * startR);
          ctx.lineTo(cx + Math.cos(a) * endR,   cy + Math.sin(a) * endR);
          glow3(ctx, sH, sa, (isMaj ? 1.2 : 0.5) + beat * 1.5);
        }
        break;
      }

      // ── 9: Helix — two spiral strands from centre to edge ─────────────────────
      case 9: {
        const PTS = 100;
        for (let strand = 0; strand < 2; strand++) {
          const phOff = strand * Math.PI;
          const hH    = ((hue0 + strand * 140) % 360 + 360) % 360;
          ctx.beginPath();
          for (let p = 0; p <= PTS; p++) {
            const frac = p / PTS;
            const ze2  = frac * frac * (3 - 2 * frac);
            const r    = maxR * ze2;
            const a    = vd.angle * 2.5 + frac * Math.PI * 4.5 + phOff
                       + Math.sin(t * 0.8 + strand * 1.3) * 0.3;
            const px   = cx + Math.cos(a) * r, py = cy + Math.sin(a) * r;
            p === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
          }
          glow3(ctx, hH, 0.40 + (energy / 255) * 0.45, 0.8 + (bass / 255) * 1.2);
        }
        break;
      }

      // ── 10: Warp speed — hyperspace streaks ───────────────────────────────────
      default: {
        const STREAKS = 55;
        for (let i = 0; i < STREAKS; i++) {
          const a       = (i / STREAKS) * Math.PI * 2 + vd.angle * 0.07 + Math.sin(i * 3.71) * 0.12;
          const z       = rings[i % rings.length];
          const ze      = z * z * (3 - 2 * z);
          const startR  = maxR * ze * 0.25;
          const endR    = Math.min(maxR * 1.05, maxR * (ze + 0.09 + (energy / 255) * 0.14));
          if (endR < 2) continue;
          const wH  = ((hue0 + (i / STREAKS) * 70) % 360 + 360) % 360;
          const wa  = ze * (0.30 + (energy / 255) * 0.60);
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a) * Math.max(1, startR), cy + Math.sin(a) * Math.max(1, startR));
          ctx.lineTo(cx + Math.cos(a) * endR, cy + Math.sin(a) * endR);
          glow3(ctx, wH, wa, 0.5 + ze * 2 + (energy / 255) * ze);
        }
      }
    }

    ctx.restore();
    ctx.globalAlpha = 1;
  }

  if (!enabled) return null;

  return createPortal(
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed', inset: 0, width: '100vw', height: '100vh',
        zIndex: -1, pointerEvents: 'none',
        // Hide the 2D canvas when a WebGL mode is active; the Three.js canvas
        // takes over (managed imperatively via _threeCanvas.style.display).
        display: WEBGL_MODES.includes(mode) ? 'none' : 'block',
      }}
    />,
    document.body
  );
}
